import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import moment from 'moment';
import numeral from 'numeral';
import { Link } from '@evertrue/et-components';
import CachedContactsResolver from 'apps/contact/components/contacts/cached-contacts-resolver';
import str from 'underscore.string';
import { fetchFromGraphql } from 'entities/helpers/gqlCall';
import proposalQueries from 'apps/proposals/proposal-queries';
import { isEmpty } from '@evertrue/sdk';

const InteractionDetails = ({
  interaction = {},
  hasInteractionCustomFields = false,
  enableInteractionSolicitorType = false,
}) => {
  const [proposal, setProposal] = useState(null);

  useEffect(() => {
    const fetchProposal = async () => {
      if (!isEmpty(interaction.proposal)) {
        setProposal(interaction.proposal);
      } else if (
        interaction.proposal_id &&
        typeof interaction.proposal_id !== 'object' &&
        !isNaN(interaction.proposal_id)
      ) {
        try {
          const response = await fetchFromGraphql(
            proposalQueries.proposal,
            { proposalId: interaction.proposal_id },
            'proposal'
          );

          const proposal = response?.data?.proposal || response?.proposal;
          if (proposal?.title) {
            setProposal(proposal);
          }
        } catch (error) {
          console.error('Error fetching proposal title:', error);
        }
      }
    };

    fetchProposal();
  }, [interaction.proposal_id, interaction.proposal]);

  const renderMetaLabel = (label, data, url) => {
    if (data) {
      const interactionDataName = data.name || data.title;
      return (
        <span className={`interaction-details--labels-item interaction-details--${label.toLowerCase()}`}>
          <span>{`${label}: `}</span>
          {url ? (
            <Link href={`/${url}/${data.id}`} title={interactionDataName}>
              {interactionDataName}
            </Link>
          ) : (
            <span className="interaction-details--value">{interactionDataName}</span>
          )}
        </span>
      );
    }
    return null;
  };

  const labels = useMemo(() => {
    let labels = hasInteractionCustomFields
      ? []
      : _.filter(interaction.label, (label) => !_.isEmpty(label.value?.trim()));

    const hasSolicitorLabel = _.find(labels, (label) => label.name?.toLowerCase()?.match(/^(solicitor|solictors)/));

    if (!hasSolicitorLabel && !_.isEmpty(interaction.solicitor)) {
      if (enableInteractionSolicitorType) {
        interaction.solicitor.forEach((solicitor) => {
          const type = solicitor.type?.toLowerCase() === 'primary' ? ' (Primary)' : '';
          labels.push({ name: 'Solicitors', value: `${solicitor.name}${type}` });
        });
      } else {
        interaction.solicitor.forEach((solicitor) => {
          labels.push({ name: 'Solicitors', value: solicitor.name });
        });
      }
    }

    if (interaction.interaction_type === 'DXO Ask') {
      labels.push({ name: 'Ask Date', value: moment(interaction.ask_date).format('MM/D/YYYY') });
      labels.push({ name: 'Ask Amount', value: numeral(interaction.ask_amount).format('$0,0.00') });
    }

    return labels;
  }, [interaction, hasInteractionCustomFields, enableInteractionSolicitorType]);

  const groups = useMemo(() => _.groupBy(labels, 'name'), [labels]);

  const updatedAt = moment(interaction.updated_at).format('MM/D/YYYY');
  const updatedBy = interaction.update_source === 'EVERTRUE' ? ' EverTrue' : ' import';
  return (
    <div className="interaction-details">
      <div className="interaction-details--header">EverTrue Details</div>
      <div className="interaction-details--content">
        <div className="interaction-details-labels">
          {renderMetaLabel('Last Update', { name: `${updatedAt} via ${updatedBy}` })}
          {renderMetaLabel('Trip', interaction.trip, 'trips')}
          {renderMetaLabel('Related Proposal', proposal, 'proposal')}
          {renderMetaLabel('Volunteer', interaction.volunteer, 'contact')}
          {renderMetaLabel('Volunteer Pool', interaction.volunteer_pool, 'volunteers/pool')}
          {interaction.remote_id && renderMetaLabel('Remote ID', { name: interaction.remote_id })}
          {interaction.purpose && renderMetaLabel('Purpose', { name: interaction.purpose })}

          {Object.entries(groups).map(([key, labelValues]) => (
            <span key={key} className="interaction-details--labels-item">
              <span>{`${str.humanize(key)}: `}</span>
              <span className="interaction-details--value">{_.pluck(labelValues, 'value').join(', ')}</span>
            </span>
          ))}

          <CachedContactsResolver ids={_.pluck(interaction.secondary_target || [], 'target_id')}>
            {({ contacts }) => {
              const secondaries = (interaction.secondary_target || []).map((target = {}) => {
                return contacts[target.target_id] || {};
              });

              return secondaries.length ? (
                <span className="interaction-details--labels-item">
                  <span>Additional Constituents: </span>
                  {secondaries.map((contact, idx) => (
                    <React.Fragment key={contact.id || idx}>
                      <Link href={`/contact/${contact.id}`} title={`profile of ${contact.name}`}>
                        {contact.name}
                      </Link>
                      {idx !== secondaries.length - 1 && ', '}
                    </React.Fragment>
                  ))}
                </span>
              ) : null;
            }}
          </CachedContactsResolver>
        </div>
      </div>
    </div>
  );
};

InteractionDetails.propTypes = {
  interaction: PropTypes.object,
  hasInteractionCustomFields: PropTypes.bool,
  enableInteractionSolicitorType: PropTypes.bool,
};

export default InteractionDetails;
